import type { ReactElement, ReactNode } from "react";
import styles from "./DataTable.module.css";

export type ColumnAlign = "start" | "end";
/** `identifier` and `numeric` switch the cell to the tabular mono treatment. */
export type ColumnFormat = "text" | "identifier" | "numeric";

export interface DataTableColumn<Row> {
  /** Stable key — also the React key for the header and cell nodes. */
  key: string;
  /** Column heading. Keep it short; it is rendered in the compact header style. */
  header: string;
  /** Renders one cell. Return `<ValueUnavailable/>` for an absent value — never `0`, never a dash. */
  cell: (row: Row) => ReactNode;
  align?: ColumnAlign;
  format?: ColumnFormat;
}

export interface DataTableProps<Row> {
  /**
   * What the table lists, as a sentence fragment ("Users across all
   * channels"). Required: it names the scroll region for assistive technology
   * and titles the table.
   */
  caption: string;
  /** Keep the caption in the accessibility tree but off screen (when a visible heading already says it). */
  captionHidden?: boolean;
  columns: readonly DataTableColumn<Row>[];
  rows: readonly Row[];
  /** Stable identity per row — never the array index. */
  rowKey: (row: Row) => string;
  /** Rendered inside the table when `rows` is empty (an `EmptyState`, typically). */
  empty?: ReactNode;
}

/**
 * The tabular primitive the read-only list views compose.
 *
 * Semantics first: a real `<table>` with a `<caption>`, `scope="col"` headers
 * and a `scope="row"` header on the first column, so a screen reader can
 * announce "row 4, userRef entra#… , channel telegram" rather than reading a
 * wall of cells.
 *
 * Wide data does not fit 375px, and shrinking six columns into a phone makes
 * the numbers unreadable. So the table lives in a horizontally scrollable
 * region that is itself keyboard-focusable and labelled — the pattern WCAG 2.2
 * expects, rather than a page that scrolls sideways as a whole.
 */
export function DataTable<Row>({
  caption,
  captionHidden = false,
  columns,
  rows,
  rowKey,
  empty,
}: DataTableProps<Row>): ReactElement {
  const captionClass = captionHidden ? styles.captionHidden : styles.caption;
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region must be keyboard-reachable (WCAG 2.1.1); role="region" + aria-label make it a named landmark rather than a stray tab stop
    <div className={styles.viewport} role="region" aria-label={caption} tabIndex={0}>
      <table className={styles.table}>
        <caption className={captionClass}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={[styles.headerCell, column.align === "end" ? styles.alignEnd : undefined]
                  .filter(Boolean)
                  .join(" ")}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={styles.emptyCell} colSpan={columns.length}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className={styles.row}>
                {columns.map((column, index) => {
                  const classes = [
                    styles.cell,
                    column.align === "end" ? styles.alignEnd : undefined,
                    column.format !== undefined && column.format !== "text"
                      ? styles.mono
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return index === 0 ? (
                    <th key={column.key} scope="row" className={classes}>
                      {column.cell(row)}
                    </th>
                  ) : (
                    <td key={column.key} className={classes}>
                      {column.cell(row)}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
